import React, { useEffect, useState } from 'react';
import {
  Link as RouterLink,
  useLocation,
  useNavigate,
  useParams
} from 'react-router-dom';
import {
  TableContainer,
  Breadcrumbs,
  Link,
  Typography,
  CircularProgress,
  IconButton,
  Tabs,
  Tab,
  Box,
} from '@mui/material';

import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import EditIcon from '@mui/icons-material/Edit';

import ClassStudentsTable from "./ClassStudentsTable";
import ClassAssignmentsTable from "./ClassAssignmentsTable";
import EditClass from "./EditClass";
import { formatDayRange, isClassActive } from "./helpers";
import { useAppState } from './AppStateContext';

function LinkRouter(props) {
  return <Link {...props} component={RouterLink} />;
}

const Class = () => {
    const { apiGet } = useAppState();

    const navigate = useNavigate();
    const location = useLocation();
    const classNameFromPreviousPage = location.state?.className;

    const { classId } = useParams();
    const [classData, setClassData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [tabValue, setTabValue] = useState(0);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const className = classData ? classData.name : classNameFromPreviousPage;
    const dateRange = classData ? formatDayRange(classData.start_date, classData.end_date) : null;

    const fetchClassData = async () => {
        setErrorMessage(null);
        try {
            const response = await apiGet(`classes/${classId}`);
            setClassData(response.data);
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        } catch (error) {
            console.error('Failed to fetch class data', error);
            if (error.response?.status === 404) {
                navigate(`/classes`, { replace: true });
            } else {
                setErrorMessage('An error occurred while trying to load the class data.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        window.scrollTo({
            top: 0,
        });
        // A different class is shown: clear the page and show the spinner.
        // Refetches for the same class keep the current page visible.
        setClassData(null);
        setIsLoading(true);
        fetchClassData();
    }, [classId]);

    const handleClose = (modified) => {
        setEditDialogOpen(false);
        if (modified) {
            fetchClassData();
        }
    };

    const handleEdit = () => {
        setEditDialogOpen(true);
    }

    return (
        <div>
            <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small" />}
                aria-label="breadcrumb"
            >
                <LinkRouter underline="hover" color="primary" to="/classes">
                    Classes
                </LinkRouter>
                <Typography color="text.primary">{className}</Typography>
            </Breadcrumbs>
            <h1>{className}
                {classData && (
                    <IconButton edge="end" sx={{ color: 'grey', ml: 1, mb: '3px' }} onClick={handleEdit} aria-label="edit">
                        <EditIcon size="28" />
                    </IconButton>
                )}
            </h1>

            {dateRange && (
              <p>
                {isClassActive(classData)
                  ? <>Active <b>{dateRange}</b>.</>
                  : <>This class is not currently active (valid <b>{dateRange}</b>).</>
                } The study times reported for the students cover this period.
              </p>
            )}

            <p>Here you’ll find a list of students in the class, as well as an overview of all assignments that have been created for this class.</p>

            <Tabs 
                value={tabValue} 
                onChange={handleTabChange} 
                aria-label="tables-tabs" 
                sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
                <Tab label="Students" />
                <Tab label="Assignments" />
            </Tabs>

            <TableContainer>
              {errorMessage && (
                  <p style={{ color: 'red', padding: '20px' }}>{errorMessage}</p>
              )}
              {isLoading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                    <CircularProgress />
                  </div>
              ) : classData ? (
                <Box p={0}>
                    {tabValue === 0 && <ClassStudentsTable fetchClassData={fetchClassData} className={className} classData={classData} />}
                    {tabValue === 1 && <ClassAssignmentsTable fetchClassData={fetchClassData} className={className} classData={classData} />}
                </Box>
              ) : null}
            </TableContainer>
            
            <EditClass open={editDialogOpen} onClose={handleClose} classData={classData} />
        </div>
    );
};

export default Class;
